import "./admin-subscription-plan-page.scss";
import {useCallback, useEffect, useState} from "react";
import {observer} from "mobx-react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {LABEL_POSITIONS, Toggle} from "../../../../components/inputs/toggle/toggle";
import BasePageLayout from "../../../../components/layouts/base-page-layout/base-page-layout";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import useNavigationBlocker from '../../../../hooks/navigation-blocker-hook';
import CardGeneral from "./card-general/card-general";
import CardPricing from "./card-pricing/card-pricing";
import CardFeatures from "./card-features/card-features";
import CardSubscriptionPlans from "./card-subscription-plans/card-subscription-plans";
import EditionInfo from "./edition-info/edition-info";
import combinedStore from "../../stores/plans-combined-store";
import {ConfirmModal} from "../../../../components/modal/components/confirm-modal/confirm-modal";
import {Button} from "../../../../components/inputs/button/button";
import {BUTTON_VARIANTS} from "../../../../components/inputs/button-variants";
import SummaryModal from "./summary-modal/summary-modal";
import {PLAN_TYPES} from "../../../../models/Plan";
import {Note, NOTE_TYPE} from "../../../../components/note/note";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";

export interface AdminPlanPageProps {
    children?: React.ReactNode;
}

const AdminPlanPage: React.FC<AdminPlanPageProps> = observer(() => {
    const { id, type } = useParams();
    const planType = !type || type === PLAN_TYPES.REGULAR ? PLAN_TYPES.REGULAR : PLAN_TYPES.CUSTOM;
    const [searchParams] = useSearchParams();
    const copyFromId = searchParams.get('copy_from_id');
    const { pathname } = useLocation();
    const vGroup = useValidationGroup();
    const navigate = useNavigate();

    const pagedListStore = combinedStore.getStore(planType);

    const store = pagedListStore.selectedPlan;

    useEffect(() => {
        if (!store) {
            if (id === 'new') {
                pagedListStore.setNewPlan(copyFromId ? Number(copyFromId) : undefined);
            }
            else {
                pagedListStore.setEditPlan(Number(id));
            }
        }
        return () => {
            if (store) {
                pagedListStore.dropSelectedPlan();
            }
        };
    }, [id, copyFromId, store, pagedListStore]);


    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const hasChanges = (): boolean => {
        return store?.hasChanges() || false;
    }

    const [isShowSaveConfirmModal, setIsShowSaveConfirmModal] = useState(false);

    const saveChanges = async () => {
        await store?.save();
        const newType = store?.is_custom ? PLAN_TYPES.CUSTOM : PLAN_TYPES.REGULAR;
        unblockNavigation();
        
        /** when failed to save should not navigate */
        if (!store?.id) {
            return;
        }

        const newPath = !type ? `${pathname}/../${store?.id}` : `${pathname}/../../${newType}/${store?.id}`
        navigate({
            pathname: newPath
        }, { replace: true });
    }

    const discardChanges = () => {
        store?.discardChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const onCanBeAssignedHandler = () => {
        if (store) {
            store.deprecated = !store.deprecated;
        }
    }

    const [isShowSummary, setIsShowSummary] = useState(false);

    return (
        <BasePageLayout title="Billing" subTitles={['Manage plans', store?.name]}>
            <EditorPageLayout
                toolbar={{
                    hasLocalesSelector: false,
                    content: (
                        <>
                            <Toggle
                                checked={!store?.deprecated}
                                label="Can be assigned"
                                labelPosition={LABEL_POSITIONS.LEFT}
                                onChange={onCanBeAssignedHandler}
                            />
                            <Toggle
                                checked={store?.on_install}
                                label="On install default"
                                labelPosition={LABEL_POSITIONS.LEFT}
                                onChange={store?.setOnInstall}
                                disabled={store?.is_custom}
                            />
                            <Toggle
                                checked={store?.is_test}
                                disabled={store?.isInUse}
                                label="Is test"
                                labelPosition={LABEL_POSITIONS.LEFT}
                                onChange={store?.setIsTest}
                            />
                            <Button
                                text="Summary"
                                variant={BUTTON_VARIANTS.Tertiary}
                                onClick={() => setIsShowSummary(true)}
                            />
                        </>
                    ),
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: () => !store?.is_custom && !store?.deprecated && store?.isChanged('deprecated')
                            ? setIsShowSaveConfirmModal(true) :
                            saveChanges(),
                        disabled: !vGroup.isValid() || !store?.canSave(),
                    },
                    isLoading: store?.isSaving() || pagedListStore.isLoading
                }}>
                <div className="manage-plans-form-content">
                    { store?.isInUse &&
                        <Note
                            type={NOTE_TYPE.INFORMATION}
                            body={<FontBody1>This plan not allowed for editing, it's already in use.</FontBody1>}
                        />
                    }
                    <CardGeneral store={pagedListStore.selectedPlan} validationGroup={vGroup} />
                    <CardPricing store={pagedListStore.selectedPlan} validationGroup={vGroup} />
                    <CardFeatures store={pagedListStore.selectedPlan} validationGroup={vGroup} />
                    <CardSubscriptionPlans store={pagedListStore.selectedPlan} validationGroup={vGroup} />
                    <EditionInfo store={pagedListStore.selectedPlan} />
                </div>
                {isShowUnsaved &&
                    <ConfirmModal
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setIsShowUnsaved(false)
                        }}
                        okButton={{
                            title: 'Discard',
                            onClick: () => {
                                discardChanges();
                                setIsShowUnsaved(false);
                                retryNavigation();
                            }
                        }}
                    />
                }
                {isShowSaveConfirmModal &&
                    <ConfirmModal
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setIsShowSaveConfirmModal(false)
                        }}
                        okButton={{
                            title: 'Save',
                            onClick: () => {
                                setIsShowSaveConfirmModal(false);
                                saveChanges();
                            }
                        }}
                        title='Warning'
                        text='"Can be assigned = Yes" plans will be immediately available for merchants in the Subscription plans page.'
                    />
                }
                {isShowSummary &&
                    <SummaryModal
                        planEditorModel={store?.getUpdatedModel()}
                        onClose={() => setIsShowSummary(false)}
                    />
                }
            </EditorPageLayout>
        </BasePageLayout>
    );
});

export default AdminPlanPage;
